import { ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
interface AlertListProps<T> {
  severity: 'info' | 'warning' | 'error' | 'success';
  message: ReactNode;
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  itemsThresholdBeforeScroll?: number;
  listMaxHeight?: number;
  sx?: SxProps<Theme>;
}

export default function AlertList<T>({
  severity,
  message,
  items,
  renderItem,
  itemsThresholdBeforeScroll,
  listMaxHeight = 180,
  sx,
}: AlertListProps<T>) {
  const theme = useTheme();
  if (items.length === 0) {
    return null;
  }

  const scrollbar = theme.palette.semantic.scrollbar[severity];

  const listSx =
    itemsThresholdBeforeScroll && items.length >= itemsThresholdBeforeScroll
      ? {
          maxHeight: listMaxHeight,
          overflowY: 'auto',
          scrollbarColor: `${scrollbar.toggle} ${scrollbar.bg}`,
          opacity: 0.6,
          '&::-webkit-scrollbar-track': {
            backgroundColor: scrollbar.bg,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: scrollbar.toggle,
            '&:hover': {
              backgroundColor: scrollbar.hover,
            },
          },
        }
      : {};

  return (
    <Alert
      severity={severity}
      sx={{
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: 'none',
        pr: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
        ...sx,
      }}
    >
      <Stack gap={1}>
        {typeof message === 'string' ? (
          <Typography>{message}</Typography>
        ) : (
          message
        )}
        <Stack gap={1} sx={listSx}>
          {items.map((item, index) => renderItem(item, index))}
        </Stack>
      </Stack>
    </Alert>
  );
}
